/**
 * Utility condivisa per le stagioni.
 * Tutte le sezioni devono usare queste funzioni per garantire uniformità.
 * Fonte centralizzata: API /api/stagioni + collection stagioni_config.
 */

import api from './api';

// Cache in-memory delle stagioni caricate dall'API
let _cachedStagioni = null;
let _cacheTimestamp = 0;
const CACHE_TTL = 30000; // 30 secondi

/**
 * Carica le stagioni dalla API centralizzata (con cache).
 * Restituisce un array di label tipo ["Primavera/Estate 2027", "Autunno/Inverno 2027", ...]
 */
export async function fetchStagioni() {
  const now = Date.now();
  if (_cachedStagioni && (now - _cacheTimestamp) < CACHE_TTL) {
    return _cachedStagioni;
  }
  try {
    const res = await api.get('/stagioni');
    const labels = res.data.map(s => s.label || `${s.stagione} ${s.anno}`);
    _cachedStagioni = labels;
    _cacheTimestamp = now;
    return labels;
  } catch {
    return getDefaultStagioni();
  }
}

/** Invalida la cache (da chiamare dopo add/delete stagione). */
export function invalidateStagioniCache() {
  _cachedStagioni = null;
  _cacheTimestamp = 0;
}

/**
 * Genera la lista standard di stagioni (4 stagioni: anno corrente e prossimo).
 * Usata come fallback se l'API non risponde.
 */
export function getDefaultStagioni() {
  const anno = new Date().getFullYear();
  return [
    `Primavera/Estate ${anno + 1}`,
    `Autunno/Inverno ${anno + 1}`,
    `Primavera/Estate ${anno}`,
    `Autunno/Inverno ${anno}`
  ];
}

/**
 * Normalizza una stagione al formato standard.
 */
function normalizeStagione(s) {
  if (!s || typeof s !== 'string') return s;
  return s
    .replace(/^P\/E\s+/i, 'Primavera/Estate ')
    .replace(/^A\/I\s+/i, 'Autunno/Inverno ')
    .trim();
}

/**
 * Unisce le stagioni provenienti da dati locali con quelle standard,
 * senza duplicati, ordinate per anno desc e poi P/E prima di A/I.
 */
export function mergeStagioni(apiStagioni = []) {
  const defaults = getDefaultStagioni();
  const normalized = apiStagioni.map(normalizeStagione);
  const allSet = new Set([...defaults, ...normalized]);
  const all = Array.from(allSet).filter(s => /\d{4}/.test(s));

  all.sort((a, b) => {
    const annoA = parseInt(a.match(/\d{4}/)?.[0] || '0');
    const annoB = parseInt(b.match(/\d{4}/)?.[0] || '0');
    if (annoA !== annoB) return annoB - annoA;
    const isPEA = a.startsWith('Primavera') ? 0 : 1;
    const isPEB = b.startsWith('Primavera') ? 0 : 1;
    return isPEA - isPEB;
  });

  return all;
}

/**
 * Restituisce la stagione di riferimento per la maggior parte delle sezioni
 * (Dashboard, Appuntamenti, Budget, Ordini Clienti, Riepilogo Clienti).
 * Legge da localStorage (popolata al boot dell'app da /api/settings/stagioni-default);
 * fallback: 'Autunno/Inverno 2027'.
 * IMPORTANTE: gestione bloccati usa invece `getStagioneBloccati()`.
 */
export function getStagioneCorrente() {
  return _readStagioneDefault('dashboard', 'Autunno/Inverno 2027');
}

/**
 * Stagione di default per Gestione Bloccati (stagione appena passata di cui
 * si stanno raccogliendo i pagamenti). Configurabile in Impostazioni.
 */
export function getStagioneBloccati() {
  return _readStagioneDefault('bloccati', 'Primavera/Estate 2027');
}

/**
 * Ritorna la stagione di default per una sezione specifica (leggendo la cache
 * salvata in localStorage). Fallback: valore hardcoded.
 */
export function getStagioneDefault(sezione, fallback = 'Autunno/Inverno 2027') {
  return _readStagioneDefault(sezione, fallback);
}

function _readStagioneDefault(sezione, fallback) {
  try {
    const raw = localStorage.getItem('stagioni_default_v1');
    if (raw) {
      const obj = JSON.parse(raw);
      if (obj && typeof obj[sezione] === 'string' && obj[sezione]) return obj[sezione];
    }
  } catch { /* noop */ }
  return fallback;
}

/**
 * Fetch delle stagioni di default dal backend e caching in localStorage.
 * Da chiamare al boot dell'app e dopo salvataggi nella pagina Impostazioni.
 */
export async function fetchStagioniDefault() {
  try {
    const res = await api.get('/settings/stagioni-default');
    const stagioni = res?.data?.stagioni;
    if (stagioni && typeof stagioni === 'object') {
      localStorage.setItem('stagioni_default_v1', JSON.stringify(stagioni));
      return stagioni;
    }
  } catch { /* fallback ai valori hardcoded */ }
  return null;
}

/**
 * Salva una nuova mappa stagione-per-sezione sul backend e aggiorna la cache.
 */
export async function saveStagioniDefault(stagioni) {
  const res = await api.put('/settings/stagioni-default', { stagioni });
  const saved = res?.data?.stagioni;
  if (saved) localStorage.setItem('stagioni_default_v1', JSON.stringify(saved));
  return saved;
}

/**
 * Abbrevia la stagione in formato compatto: "PE27" o "AI27".
 * Accetta: "Primavera/Estate 2027", "P/E 2027", "PE 2027", "PE2027", ecc.
 */
export function abbreviaStagione(stagione) {
  if (!stagione || typeof stagione !== 'string') return '-';
  const s = stagione.trim();
  const match = s.match(/^(Primavera\/Estate|Autunno\/Inverno|P\/E|A\/I|PE|AI)\s*(\d{4})$/i);
  if (!match) return s;
  const tipo = match[1].toLowerCase().startsWith('p') ? 'PE' : 'AI';
  const anno = match[2].slice(-2);
  return `${tipo}${anno}`;
}
